import * as THREE from 'three';
import { events } from '../core/events.js';
import { groundRadius } from './placement.js';

// Each active pickup: { id, name, color, icon, group, mesh, worldPos, phase }.
// Kept in a Set so collection (delete) is O(1) and frame updates iterate all.
const pickups = new Set();
const UP_Y = new THREE.Vector3(0, 1, 0);

// Different silhouettes so crystals and comm parts are visually distinguishable
// at a glance. All share a floating gem-like halo below on the ground.
function buildMesh(color, shape) {
  const material = new THREE.MeshStandardMaterial({
    color,
    emissive: color,
    emissiveIntensity: 0.85,
    metalness: 0.35,
    roughness: 0.25,
  });

  let mesh;
  switch (shape) {
    case 'dish':
      mesh = new THREE.Mesh(new THREE.ConeGeometry(0.22, 0.12, 16), material);
      mesh.rotation.x = Math.PI;   // open side up — reads as a radar dish
      break;
    case 'cell':
      mesh = new THREE.Mesh(new THREE.CapsuleGeometry(0.075, 0.22, 6, 12), material);
      break;
    case 'board':
      mesh = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.04, 0.2), material);
      break;
    case 'chip':
      mesh = new THREE.Mesh(new THREE.BoxGeometry(0.14, 0.08, 0.14), material);
      break;
    case 'bloom': {
      // Rooted flower — stem grows from the ground, bloom sits at the
      // top. Marked `rooted` so updatePickups swaps the floating bob for
      // a gentle wind-like sway.
      mesh = new THREE.Group();
      mesh.userData.rooted = true;

      const stemMat = new THREE.MeshStandardMaterial({
        color: 0x3a6448, roughness: 0.9, flatShading: true,
      });

      // Stem — thin cylinder from ground to bloom height.
      const stem = new THREE.Mesh(
        new THREE.CylinderGeometry(0.014, 0.02, 0.45, 6),
        stemMat,
      );
      stem.position.y = 0.225;
      stem.castShadow = true;
      mesh.add(stem);

      // Two drooping leaves partway up the stem.
      for (let i = 0; i < 2; i++) {
        const a = (i / 2) * Math.PI * 2 + 0.4;
        const leaf = new THREE.Mesh(
          new THREE.SphereGeometry(0.05, 8, 6), stemMat,
        );
        leaf.scale.set(1.9, 0.25, 0.9);
        leaf.position.set(Math.cos(a) * 0.07, 0.18, Math.sin(a) * 0.07);
        leaf.rotation.y = a;
        leaf.rotation.z = -0.35;
        leaf.castShadow = true;
        mesh.add(leaf);
      }

      // Bloom head — central core with four petal-bulbs.
      const core = new THREE.Mesh(
        new THREE.SphereGeometry(0.1, 14, 10), material,
      );
      core.position.y = 0.5;
      core.castShadow = true;
      mesh.add(core);
      for (let i = 0; i < 4; i++) {
        const a = (i / 4) * Math.PI * 2;
        const petal = new THREE.Mesh(
          new THREE.SphereGeometry(0.07, 10, 8), material,
        );
        petal.position.set(Math.cos(a) * 0.13, 0.5, Math.sin(a) * 0.13);
        petal.castShadow = true;
        mesh.add(petal);
      }
      break;
    }
    case 'gem':
    default:
      mesh = new THREE.Mesh(new THREE.OctahedronGeometry(0.16, 0), material);
  }
  // Rooted pickups (flowers growing from the ground) sit at y=0 and use
  // per-child shadow flags; floating pickups hover at y=0.45 and bob.
  mesh.position.y = mesh.userData.rooted ? 0 : 0.45;
  mesh.castShadow = true;
  return mesh;
}

// Vertical light pillar used as a distance cue. Open-ended cylinder with a
// shader that (a) makes it hollow by brightening only at the silhouette rim
// via fresnel, (b) stays transparent near the ground so the item itself isn't
// obscured, and (c) fades to zero at the top.
function buildBeacon(color) {
  const height = 5;
  const radius = 0.09;
  const geo = new THREE.CylinderGeometry(radius, radius, height, 24, 1, true);
  geo.translate(0, height / 2, 0);   // anchor bottom at y=0

  const mat = new THREE.ShaderMaterial({
    transparent: true,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    side: THREE.DoubleSide,
    uniforms: {
      uColor:  { value: new THREE.Color(color) },
      uHeight: { value: height },
    },
    vertexShader: /* glsl */`
      varying float vY;
      varying vec3 vN;
      varying vec3 vV;
      void main() {
        vY = position.y;
        vec4 mv = modelViewMatrix * vec4(position, 1.0);
        vN = normalize(normalMatrix * normal);
        vV = normalize(-mv.xyz);
        gl_Position = projectionMatrix * mv;
      }
    `,
    fragmentShader: /* glsl */`
      uniform vec3 uColor;
      uniform float uHeight;
      varying float vY;
      varying vec3 vN;
      varying vec3 vV;
      void main() {
        float t = clamp(vY / uHeight, 0.0, 1.0);

        // Skip the first ~15% so the item underneath stays readable.
        float bottomFade = smoothstep(0.14, 0.3, t);
        // Non-linear fall-off as the beam reaches the top.
        float topFade    = pow(1.0 - t, 1.6);

        // Fresnel rim — brighter where the cylinder's surface is edge-on to
        // the camera, near-transparent where it's facing the camera. Turns
        // the solid column into a hollow tube of light.
        float rim = 1.0 - abs(dot(vN, vV));
        float fresnel = pow(rim, 1.4);

        float alpha = bottomFade * topFade * (0.08 + 0.5 * fresnel);
        gl_FragColor = vec4(uColor, alpha);
      }
    `,
  });

  const mesh = new THREE.Mesh(geo, mat);
  mesh.renderOrder = 1;   // draw after opaque scene so additive blend reads
  return mesh;
}

function buildPickupGroup(color, shape, beacon) {
  const g = new THREE.Group();

  const mesh = buildMesh(color, shape);
  g.add(mesh);

  // Soft halo disc on the ground below the item
  const halo = new THREE.Mesh(
    new THREE.RingGeometry(0.22, 0.28, 40),
    new THREE.MeshBasicMaterial({
      color,
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0.35,
      depthWrite: false,
      fog: false,
    }),
  );
  halo.position.y = 0.02;
  halo.rotation.x = Math.PI / 2;
  g.add(halo);

  if (beacon) g.add(buildBeacon(color));

  return { group: g, mesh };
}

export function spawnPickup(scene, planet, {
  id, name, color, shape = 'gem', icon, beacon = false, dir, locked = false,
}) {
  const { group, mesh } = buildPickupGroup(color, shape, beacon);
  const direction = dir ?? new THREE.Vector3().randomDirection();
  group.quaternion.setFromUnitVectors(UP_Y, direction);
  group.position.copy(direction).multiplyScalar(groundRadius(planet, direction));
  scene.add(group);

  const entry = {
    id, name, color, icon,
    group, mesh,
    worldPos: group.position.clone(),
    phase: Math.random() * Math.PI * 2,
    locked,
  };
  pickups.add(entry);
  return entry;
}

// Bob + spin every pickup so the scene feels alive when you're standing still.
// Rooted pickups (flowers) sway in place instead of bobbing; their origin
// sits on the ground so the stem stays anchored.
export function updatePickups() {
  const t = performance.now() / 1000;
  for (const p of pickups) {
    if (p.mesh.userData.rooted) {
      p.mesh.rotation.x = Math.sin(t * 0.7 + p.phase) * 0.08;
      p.mesh.rotation.z = Math.cos(t * 0.9 + p.phase * 1.3) * 0.08;
      continue;
    }
    p.mesh.rotation.y = t * 1.5 + p.phase;
    p.mesh.position.y = 0.45 + Math.sin(t * 2 + p.phase) * 0.08;
  }
}

export function findNearestPickup(worldPos, maxDist = 1.8) {
  let best = null;
  let bestD = maxDist * maxDist;
  for (const p of pickups) {
    if (p.locked) continue;
    const d = p.worldPos.distanceToSquared(worldPos);
    if (d < bestD) { best = p; bestD = d; }
  }
  return best;
}

export function collectPickup(entry) {
  if (!pickups.has(entry)) return;
  pickups.delete(entry);
  entry.group.parent?.remove(entry.group);
  events.emit('pickup:collected', entry);
}

// Silent removal — takes the pickup out of the scene and registry without
// triggering any collection events. Used when a pickup is "consumed" by
// something other than the player (e.g., a creature eating a thrown
// crystal).
export function removePickup(entry) {
  if (!entry || !pickups.has(entry)) return;
  pickups.delete(entry);
  entry.group.parent?.remove(entry.group);
}
